import React, { useEffect, useState } from "react";
import { Boxes, CheckCircle2, AlertTriangle, Lock, Settings2, Unplug, RefreshCw } from "lucide-react";
import { Card, CardContent, Button, Input, StatusBadge, LoadingBlock, Modal, toast } from "@/components/ds";
import { api, formatApiError, type IntegrationCatalog, type IntegrationProvider } from "@/lib/api";

const STATUS: Record<string, { tone: "green" | "amber" | "red" | "slate"; label: string }> = {
  connected: { tone: "green", label: "Connected" },
  configuration_required: { tone: "amber", label: "Configuration required" },
  error: { tone: "red", label: "Not verified" },
  disconnected: { tone: "slate", label: "Not connected" },
};
const CAT: Record<string, string> = { crm: "CRM", fsm: "Field Service", calendar: "Calendar", custom: "Custom" };

export function IntegrationMarketplace() {
  const [cat, setCat] = useState<IntegrationCatalog | null>(null);
  const [active, setActive] = useState<IntegrationProvider | null>(null);

  const load = () => api.ciCatalog().then(setCat).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  if (!cat) return <LoadingBlock rows={4} />;

  return (
    <div className="space-y-4" data-testid="integration-marketplace">
      <div className="flex items-center gap-2">
        <Boxes className="h-5 w-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-slate-900">Integration marketplace</h2>
      </div>
      <p className="text-sm text-slate-500">Connect your CRM and field-service systems. An integration is only marked <b>Connected</b> after we genuinely verify your credentials with the provider — we never fake a connection. Data syncing (customers, leads, appointments, jobs, technicians, service types) is coming soon and is not active yet.</p>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="marketplace-grid">
        {cat.providers.map((p) => {
          const s = STATUS[p.connection.status] ?? STATUS.disconnected;
          return (
            <Card key={p.key}><CardContent className="flex h-full flex-col gap-3 p-4" data-testid={`integration-card-${p.key}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{p.name}</h3>
                  <p className="text-xs text-slate-400">{CAT[p.category] ?? p.category}</p>
                </div>
                {p.available
                  ? <StatusBadge tone={s.tone} data-testid={`integration-status-${p.key}`}>{s.label}</StatusBadge>
                  : <StatusBadge tone="slate"><Lock className="mr-1 inline h-3 w-3" />{p.min_plan}+</StatusBadge>}
              </div>
              <p className="text-xs text-slate-500">Syncs (soon): {p.sync.join(", ")}</p>
              {p.connection.detail && p.connection.status !== "connected" && (
                <p className="text-[11px] text-amber-600" data-testid={`integration-detail-${p.key}`}>{p.connection.detail}</p>
              )}
              <div className="mt-auto flex gap-2 pt-1">
                {!p.available ? (
                  <Button size="sm" variant="outline" disabled data-testid={`integration-locked-${p.key}`}><Lock className="h-3.5 w-3.5" /> Upgrade to {p.min_plan}</Button>
                ) : p.key === "google_calendar" ? (
                  <span className="text-xs text-slate-400">{p.managed_note}</span>
                ) : p.fields.length === 0 ? (
                  <span className="text-xs text-slate-400">{p.managed_note || "Contact ResFil to enable."}</span>
                ) : (
                  <>
                    <Button size="sm" onClick={() => setActive(p)} data-testid={`integration-configure-${p.key}`}>
                      <Settings2 className="h-3.5 w-3.5" /> {p.connection.status === "connected" ? "Manage" : "Configure"}
                    </Button>
                    {p.connection.status !== "disconnected" && (
                      <Button size="sm" variant="ghost" onClick={async () => { try { await api.ciDisconnect(p.key); toast.success("Disconnected"); load(); } catch (e) { toast.error(formatApiError(e)); } }} data-testid={`integration-disconnect-${p.key}`}><Unplug className="h-3.5 w-3.5" /></Button>
                    )}
                  </>
                )}
              </div>
            </CardContent></Card>
          );
        })}
      </div>

      {active && <ConfigureModal provider={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />}
    </div>
  );
}

function ConfigureModal({ provider, onClose, onDone }: { provider: IntegrationProvider; onClose: () => void; onDone: () => void }) {
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ status: string; detail: string } | null>(null);

  const connect = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.ciConnect(provider.key, creds);
      setResult({ status: r.status, detail: r.detail });
      if (r.status === "connected") { toast.success(`${provider.name} connected & verified`); setTimeout(onDone, 900); }
      else toast.warning(`Not connected: ${r.detail}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Connect ${provider.name}`} description="We verify these credentials with the provider before marking the integration Connected." testid="integration-configure-modal">
      <div className="space-y-3">
        {provider.fields.map((f) => (
          <div key={f.key}>
            <p className="mb-1 text-xs font-medium text-slate-500">{f.label}</p>
            <Input type={f.secret ? "password" : "text"} value={creds[f.key] ?? ""} onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })} data-testid={`integration-field-${f.key}`} />
          </div>
        ))}
        {result && (
          <div className={`flex items-start gap-2 rounded-lg border p-3 text-xs ${result.status === "connected" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid="integration-result">
            {result.status === "connected" ? <CheckCircle2 className="mt-0.5 h-4 w-4" /> : <AlertTriangle className="mt-0.5 h-4 w-4" />}
            <span>{result.status === "connected" ? "Verified & connected." : result.detail}</span>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={connect} disabled={busy} data-testid="integration-connect-btn"><RefreshCw className="h-3.5 w-3.5" /> Verify & connect</Button>
        </div>
      </div>
    </Modal>
  );
}
